import React, { useEffect, useRef } from 'react';
import logoImg from '@/assets/fexlogistics-logo.jpg';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import { getApiBaseUrl } from '@/services/apiBase';

const inferTypeFromCode = (code) => {
  const c = String(code || '').toUpperCase();
  const idx = c.startsWith('FX') ? 2 : 0;
  const lead = c[idx];
  if (lead === '1') return 'local';
  if (lead === '2') return 'interstate';
  if (lead === '3') return 'international';
  return 'interstate';
};

const shortTrackingCode = (code) => {
  const c = String(code || '').toUpperCase();
  const lead = c.startsWith('FX') ? c[2] : c[0];
  const fnvPrime = 16777619;
  let hash = 2166136261;
  for (let i = 0; i < c.length; i++) {
    hash ^= c.charCodeAt(i);
    hash = (hash * fnvPrime) >>> 0;
  }
  const num = String(hash % 1000000).padStart(6, '0');
  return `FX${lead}${num}`;
};

const safe = (v) => (v && typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''));

const normalizeLocation = (loc, serviceType) => {
  const s = safe(loc).toLowerCase();
  if (!s) return '';

  const lagosAreas = [
    'ikeja', 'surulere', 'lagos island', 'ikoyi', 'victoria island', 'lekki', 'ajah',
    'sangotedo', 'iyana ipaja', 'egbeda', 'agege', 'alimosho', 'maryland', 'gbagada',
    'oshodi', 'anthony', 'ejigbo', 'isolo', 'itire', 'alausa', 'oregun', 'lagos'
  ];

  // For international, just return the capitalized country/location name
  if (serviceType === 'international') {
    return safe(loc).split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }

  // For local or locations recognized as Lagos areas, return specific area if found, otherwise Lagos State
  if (serviceType === 'local' || lagosAreas.some(area => s.includes(area))) {
    const foundArea = lagosAreas.find(area => s.includes(area));
    if (foundArea && foundArea !== 'lagos') {
      return foundArea.charAt(0).toUpperCase() + foundArea.slice(1) + ', Lagos';
    }
    return 'Lagos State';
  }

  // Return the capitalized location if not Lagos
  return safe(loc).split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

export default function AgentReceipt({ shipment, typeHint }) {
  const printable = shipment || {};
  const type = typeHint || printable.serviceType || inferTypeFromCode(printable.trackingCode);

  // Server maps location fields differently per service type:
  //   local      → pickupLocation (sender), receiverCity (receiver)
  //   interstate → senderState,             receiverState
  //   international →                       country
  const originRaw = (
    printable.origin ||
    printable.pickupLocation ||
    printable.senderState ||
    printable.senderAddress ||
    ''
  );
  const destinationRaw = (
    printable.destination ||
    printable.receiverCity ||
    printable.receiverState ||
    printable.country ||
    printable.dropoffLocation ||
    printable.receiverAddress ||
    ''
  );

  const origin = normalizeLocation(originRaw, type);
  const destination = normalizeLocation(destinationRaw, type);

  const itemType = (() => {
    const d = (printable.deliveryType || '').toString();
    if (d) return d.charAt(0).toUpperCase() + d.slice(1);
    if ((printable.serviceType || type) === 'international' && printable.internationalSubtype) {
      const s = printable.internationalSubtype.toString();
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
    const t = (printable.serviceType || type || '').toString();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
  })();

  const trackingRaw = safe(printable.trackingCode);
  const trackingShort = shortTrackingCode(trackingRaw);

  const paymentInfo = (() => {
    const isPrepaid = (printable.paymentType || '').toLowerCase() === 'paynow' || (printable.paymentType || '').toLowerCase() === 'online';
    const amount = typeof printable.price === 'number' ? `₦${printable.price.toLocaleString()}` : '₦0.00';

    if (isPrepaid) {
      return { label: 'Paid', amount };
    }

    // If not prepaid, check if it's COD
    const isCOD = (printable.paymentType || '').toLowerCase() === 'cashondelivery' || (printable.paymentType || '').toLowerCase() === 'ondelivery';
    const codAmt = printable.codAmount ? `₦${Number(printable.codAmount).toLocaleString()}` : amount;

    return {
      label: isCOD ? 'COD' : 'To Collect',
      amount: codAmt
    };
  })();

  const custCat = safe(printable.customerCategory) || 'Regular';

  // Robust weight handling
  const rawWeight = printable.weight || printable.package?.weight;
  const weightLabel = (rawWeight && !isNaN(parseFloat(rawWeight)))
    ? `${parseFloat(rawWeight)} Kg`
    : (safe(rawWeight) || '—');

  // Robust content handling
  const content = printable.contentList || printable.package?.contentList || printable.shipmentContent || '';

  const trackingUrl = `${getApiBaseUrl()}/shipments/track/${encodeURIComponent(trackingRaw || trackingShort)}`;

  const barcodeRef = useRef(null);

  useEffect(() => {
    try {
      const code = trackingRaw || trackingShort;
      if (barcodeRef.current && code) {
        JsBarcode(barcodeRef.current, code, { format: 'CODE128', width: 2, height: 44, displayValue: false, margin: 0 });
      }
    } catch { }
  }, [trackingRaw, trackingShort]);

  return (
    <div className="hidden print:block bg-white p-6 relative receipt-print border border-gray-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex flex-col">
          <img src={logoImg} alt="Fex Logistics" className="h-12 w-auto object-contain mb-1" />
          <p className="text-[9px] text-black font-medium">www.fexlogistics.com.ng</p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right text-[11px] space-y-0.5 text-black">
            <div><span className="font-bold uppercase tracking-tighter mr-1">Date:</span> {new Date(printable.createdAt || Date.now()).toLocaleDateString()}</div>
            <div><span className="font-bold uppercase tracking-tighter mr-1">Waybill:</span> {trackingShort}</div>
            <div><span className="font-bold uppercase tracking-tighter mr-1">{paymentInfo.label}:</span> <span className="text-black font-black text-base">{paymentInfo.amount}</span></div>
          </div>
          <div className="flex flex-col items-center bg-white p-1 border border-gray-100 rounded">
            <svg ref={barcodeRef} width={140} height={40} style={{ border: 'none' }} />
            <p className="text-[9px] font-bold tracking-[0.2em] mt-1 text-black">{trackingRaw}</p>
          </div>
        </div>
      </div>

      <div className="bg-black h-1.5 w-full rounded-full mb-4"></div>

      <div className="flex justify-between items-center mb-4 px-2">
        <div className="flex flex-col flex-1">
          <p className="text-[11px] font-bold text-black uppercase tracking-widest mb-1">Origin</p>
          <p className="text-2xl font-black text-black leading-none">{safe(origin)}</p>
        </div>
        <div className="flex flex-col items-center justify-center px-4">
          <div className="text-black text-3xl font-black">→</div>
          <p className="text-[9px] font-bold text-black uppercase tracking-tighter">{safe(itemType || type)}</p>
        </div>
        <div className="flex flex-col flex-1 text-right">
          <p className="text-[11px] font-bold text-black uppercase tracking-widest mb-1">Destination</p>
          <p className="text-2xl font-black text-black leading-none">{safe(destination)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6">
        <div className="bg-white rounded-xl p-4 border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-black"></div>
          <p className="text-[11px] font-bold text-black uppercase tracking-widest mb-2">Sender Details</p>
          <p className="text-base font-black text-black mb-0.5">{safe(printable.senderName)}</p>
          <p className="text-sm font-bold text-black">{safe(printable.senderNumber || printable.senderPhone)}</p>
          <p className="text-[10px] text-gray-700 font-medium mt-1 leading-tight line-clamp-2">{safe(printable.senderAddress)}</p>
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-[10px] text-black font-bold uppercase mb-1">Category</p>
            <p className="text-sm font-bold text-black">{custCat}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-black"></div>
          <p className="text-[11px] font-bold text-black uppercase tracking-widest mb-2">Receiver Details</p>
          <p className="text-base font-black text-black mb-0.5">{safe(printable.receiverName)}</p>
          <p className="text-sm font-bold text-black">{safe(printable.receiverNumber || printable.receiverPhone)}</p>
          <p className="text-[10px] text-gray-700 font-medium mt-1 leading-tight line-clamp-2">{safe(printable.receiverAddress)}</p>
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-[10px] text-black font-bold uppercase mb-1">Weight</p>
            <p className="text-sm font-bold text-black">{weightLabel}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-4">
        <div className="col-span-2 bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-[11px] font-bold text-black uppercase tracking-widest mb-2">Package Contents</p>
          <p className="text-sm font-medium text-black italic leading-relaxed">
            {safe(content) || 'No content description provided.'}
          </p>
        </div>
        <div className="flex flex-col items-center justify-center bg-white border border-gray-100 rounded-xl p-2 shadow-sm">
          <QRCodeSVG value={trackingUrl} size={80} />
          <p className="text-[9px] font-black text-black uppercase mt-2 tracking-tighter text-center leading-tight">
            Scan to Track<br />Live Updates
          </p>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-white border border-gray-100 rounded-lg flex items-center justify-center">
            <QRCodeSVG value={`${window.location.origin}/contact`} size={32} />
          </div>
          <div>
            <p className="text-[10px] font-bold text-black">Support & Feedback</p>
            <p className="text-[9px] text-black">07046674299 | support@fexlogistics.com.ng</p>
          </div>
        </div>
        <div className="text-right italic text-[9px] text-black font-medium">
          Thank you for choosing Fex Logistics. Faster. Better. Reliable.
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { margin: 0; padding: 0; }
          .receipt-print {
            width: 148mm;
            height: 210mm;
            margin: 0 auto;
            padding: 10mm;
            page-break-inside: avoid;
            box-shadow: none;
            border: none;
          }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
